import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

// Linia postępu doboru (prawa krawędź) — szklana karta, ikona kategorii przy każdym
// kroku, wartości wymaganych pól pod etykietą. TYLKO desktop (hidden xl:flex): na
// mobile/tablecie pasek by zasłaniał formularz.
// Świadomie BEZ animate-ping (ciągła animacja kompozytora muliła front) — „current"
// ma statyczny glow + pulse na kropce.
// steps: [{ id, label, icon, detail, required, status: 'done'|'current'|'todo', onClick }]
const ProgressRail = ({ steps }) => {
  const { t } = useTranslation('asystent');
  return (
  <nav
    aria-label={t('postep.aria')}
    className="hidden xl:flex fixed right-5 top-1/2 -translate-y-1/2 z-10 flex-col items-end
               rounded-2xl border border-gray-200/80 bg-white/85 backdrop-blur-sm shadow-lg shadow-gray-200/50 px-5 py-5"
  >
    {steps.map((s, i) => {
      const last = i === steps.length - 1;
      const done = s.status === 'done';
      const current = s.status === 'current';
      return (
        <React.Fragment key={s.id}>
          <button
            type="button"
            onClick={s.onClick}
            className="group flex items-center gap-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
            aria-current={current ? 'step' : undefined}
          >
            <span className="flex flex-col items-end">
              <span
                className={`whitespace-nowrap text-[15px] leading-tight text-right transition-colors duration-300 group-hover:text-blue-600 ${
                  done ? 'text-blue-600 font-semibold'
                    : current ? 'text-gray-900 font-bold'
                      : 'text-gray-400 font-medium'
                }`}
              >
                {s.label}
                {s.required && <span className="text-blue-500 ml-0.5" title={t('postep.krok_wymagany')}>*</span>}
              </span>
              {s.detail && (
                <span
                  className={`whitespace-nowrap text-xs leading-tight text-right mt-0.5 tabular-nums ${
                    done ? 'text-blue-500' : 'text-gray-400'
                  }`}
                >
                  {s.detail}
                </span>
              )}
            </span>

            {/* Ikona kategorii — kolor niesie status, żeby czytać krok bez patrzenia na kropkę. */}
            <span
              className={`flex items-center justify-center h-8 w-8 shrink-0 rounded-lg transition-colors duration-300 ${
                done ? 'bg-blue-50 text-blue-600'
                  : current ? 'bg-blue-600 text-white shadow-sm shadow-blue-300/60'
                    : 'bg-gray-50 text-gray-300 group-hover:text-gray-400'
              }`}
            >
              <FontAwesomeIcon icon={s.icon} className="text-sm" />
            </span>

            {/* Stały kontener 34px — linia łącząca zawsze wyśrodkowana pod kropką. */}
            <span className="relative flex items-center justify-center h-[34px] w-[34px] shrink-0">
              <span
                className={`relative flex items-center justify-center rounded-full transition-all duration-300 ${
                  done
                    ? 'h-[34px] w-[34px] bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md shadow-blue-300/60'
                    : current
                      ? 'h-[34px] w-[34px] bg-white border-[3px] border-blue-600 shadow-[0_0_0_5px_rgba(59,130,246,0.15)]'
                      : 'h-4 w-4 bg-gray-300 group-hover:bg-gray-400'
                }`}
              >
                {done && <FontAwesomeIcon icon={faCheck} className="text-sm" />}
                {current && <span className="h-3 w-3 rounded-full bg-blue-600 animate-pulse" />}
              </span>
            </span>
          </button>
          {!last && (
            <span
              className={`w-[3px] h-10 rounded-full mr-[15.5px] my-1 transition-colors duration-500 ${
                done ? 'bg-gradient-to-b from-blue-500 to-blue-300' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </nav>
  );
};

// memo: pasek nie musi się renderować przy zmianach pól, które nie wpływają na kroki
// (progressSteps w KitAssistant jest memoizowane — referencja zmienia się tylko realnie).
export default React.memo(ProgressRail);
